import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import {
  IconArrowBackUp,
  IconChevronDown,
  IconChevronRight,
  IconPaperclip,
} from "@tabler/icons-react";
import { FileWithPath } from "@mantine/dropzone";
import { SupportTicket } from "@/app/@types/support";
import { capitalized } from "@/app/_helpers/string";
import {
  SUPPORT_STATUSES,
  getSupportCategory,
} from "@/app/_constants/support";
import {
  canReopenSupportTicket,
  SUPPORT_REOPEN_WINDOW_DAYS,
} from "@/app/_firebase/support";
import useSupportTicketEvents from "@/app/_hooks/support/useSupportTicketEvents";
import useAddSupportReply from "@/app/_hooks/support/useAddSupportReply";
import useReopenSupportTicket from "@/app/_hooks/support/useReopenSupportTicket";
import useMarkSupportTicketRead from "@/app/_hooks/support/useMarkSupportTicketRead";
import useFormatDate from "@/app/_hooks/useFormatDate";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import FileUploader from "@/app/_components/FileUploader/FileUploader";
import SupportStatusBadge from "./SupportStatusBadge";
import SupportAttachments from "./SupportAttachments";
import SupportEventTimeline from "./SupportEventTimeline";
import SupportFooter from "./SupportFooter";

const REPLY_MIN_LENGTH = 2;

interface SupportTicketDetailProps {
  ticket: SupportTicket;
}

const SupportTicketDetail = ({ ticket }: SupportTicketDetailProps) => {
  const formatDate = useFormatDate();
  const setAlert = useSetAlert();
  const { events, isLoading } = useSupportTicketEvents(ticket.uid);
  const { mutateAsync: addReply, isPending: isReplying } = useAddSupportReply();
  const { mutateAsync: reopen, isPending: isReopening } =
    useReopenSupportTicket();
  const markRead = useMarkSupportTicketRead();

  const [replyBody, setReplyBody] = useState("");
  const [replyFiles, setReplyFiles] = useState<FileWithPath[]>([]);
  // Composer is collapsed behind a Reply button (help-desk pattern, not
  // chat): opening it is a deliberate act, and it closes again after send.
  const [composerOpen, setComposerOpen] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [uploaderKey, setUploaderKey] = useState(0);
  // Start collapsed and decide once after the thread loads: expanded only
  // when there are no replies yet. Deciding per-render caused a visible
  // expand-then-collapse jump while events streamed in.
  const [showOriginal, setShowOriginal] = useState(false);
  const [originalDecided, setOriginalDecided] = useState(false);

  const category = getSupportCategory(ticket.category);
  const statusMeta = SUPPORT_STATUSES[ticket.status] ?? SUPPORT_STATUSES.open;
  const isResolved = ticket.status === "resolved";
  const reopenable = canReopenSupportTicket(ticket);

  // Opening the ticket clears the unread flag for the user.
  useEffect(() => {
    if (ticket.unreadForUser) {
      markRead(ticket.uid);
    }
  }, [ticket.uid, ticket.unreadForUser, markRead]);

  useEffect(() => {
    if (isLoading || originalDecided) return;
    setShowOriginal(events.length === 0);
    setOriginalDecided(true);
  }, [isLoading, originalDecided, events.length]);

  // Text or images: an attachment-only reply ("here's the screenshot") is fine.
  const canSendReply =
    replyBody.trim().length >= REPLY_MIN_LENGTH || replyFiles.length > 0;

  const handleReply = async () => {
    const body = replyBody.trim();
    if (!canSendReply) return;
    try {
      await addReply({ ticket, body, files: replyFiles as File[] });
      setReplyBody("");
      setReplyFiles([]);
      setShowAttach(false);
      setComposerOpen(false);
      setUploaderKey((k) => k + 1);
    } catch (error) {
      setAlert({
        type: "error",
        title: "Could not add your reply",
        content: (
          <Text size="sm">
            {error instanceof Error ? error.message : "Please try again."}
          </Text>
        ),
      });
    }
  };

  const handleReopen = async () => {
    try {
      await reopen(ticket);
    } catch (error) {
      setAlert({
        type: "error",
        title: "Could not reopen this ticket",
        content: (
          <Text size="sm">
            {error instanceof Error ? error.message : "Please try again."}
          </Text>
        ),
      });
    }
  };

  return (
    <>
      <Box flex={1} mih={0}>
        <Box h="100%" style={{ overflowY: "auto", overflowX: "hidden" }}>
          <Stack gap="md" px="md" pb="md" pt={0}>
            <Stack gap={8} pt={4}>
              <Text size="sm" fw={600}>
                {category.label}
                {ticket.affectedAreas?.length
                  ? ` · ${ticket.affectedAreas.join(", ")}`
                  : ""}
              </Text>
              {ticket.relatedJobs?.length > 0 && (
                <Group gap="xs" wrap="wrap">
                  {ticket.relatedJobs.map((job) => (
                    <Badge key={job.jobUid} variant="outline" radius="sm">
                      Job #{job.jobId}
                      {job.mountIds.length > 0
                        ? ` · ${job.mountIds.join(", ")}`
                        : ""}
                    </Badge>
                  ))}
                </Group>
              )}
              <Text size="xs" c="dimmed">
                Opened {formatDate(ticket.createdAt)}
                {ticket.createdBy?.fullName
                  ? ` by ${capitalized(ticket.createdBy.fullName)}`
                  : ""}
              </Text>
              {ticket.status === "waiting_on_you" && (
                <Text size="xs" fw={600}>
                  {statusMeta.description}
                </Text>
              )}
            </Stack>

            <Paper withBorder radius="md" p="sm">
              <UnstyledButton
                onClick={() => setShowOriginal((v) => !v)}
                w="100%"
                aria-expanded={showOriginal}
              >
                <Group gap="xs" justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={700} truncate="end" flex={1} miw={0}>
                    {ticket.subject}
                  </Text>
                  {showOriginal ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )}
                </Group>
              </UnstyledButton>
              <Collapse in={showOriginal}>
                <Text size="sm" mt="xs" style={{ whiteSpace: "pre-wrap" }}>
                  {ticket.message}
                </Text>
                <SupportAttachments urls={ticket.files} />
              </Collapse>
            </Paper>

            {isLoading ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : events.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="sm">
                No replies yet.
              </Text>
            ) : (
              <SupportEventTimeline events={events} perspective="user" />
            )}
          </Stack>
        </Box>
      </Box>

      <SupportFooter>
        {isResolved ? (
          <Stack gap="xs">
            <Alert color="green" variant="light" radius="md" p="sm">
              <Text size="sm">
                This ticket is resolved.{" "}
                {reopenable
                  ? "If the problem has come back you can reopen it."
                  : `Tickets can be reopened for ${SUPPORT_REOPEN_WINDOW_DAYS} days after being resolved. Please open a new ticket if you still need help.`}
              </Text>
            </Alert>
            {reopenable && (
              <Button
                variant="default"
                onClick={handleReopen}
                loading={isReopening}
                fullWidth
              >
                Reopen ticket
              </Button>
            )}
          </Stack>
        ) : !composerOpen ? (
          <Button
            variant="default"
            leftSection={<IconArrowBackUp size={16} />}
            onClick={() => setComposerOpen(true)}
            fullWidth
          >
            Reply
          </Button>
        ) : (
          <Stack gap="xs">
            <Textarea
              placeholder="Add a reply. Include any new details that might help."
              value={replyBody}
              onChange={(e) => setReplyBody(e.currentTarget.value)}
              autosize
              minRows={4}
              maxRows={10}
              disabled={isReplying}
              data-autofocus
              autoFocus
            />
            <Collapse in={showAttach}>
              <Box mih={140} key={uploaderKey}>
                <FileUploader
                  imagesOnly
                  compact
                  onFileResolve={(files) =>
                    setReplyFiles([...(files as FileWithPath[])])
                  }
                  dropzoneProps={{
                    styles: {
                      root: { minHeight: 120 },
                      inner: { pointerEvents: "all" },
                    },
                  }}
                />
              </Box>
            </Collapse>
            <Group justify="space-between" wrap="nowrap">
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                leftSection={<IconPaperclip size={14} />}
                onClick={() => setShowAttach((v) => !v)}
              >
                {showAttach ? "Hide images" : "Attach images"}
                {replyFiles.length > 0 ? ` (${replyFiles.length})` : ""}
              </Button>
              <Group gap="xs" wrap="nowrap">
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    setComposerOpen(false);
                    setShowAttach(false);
                  }}
                  disabled={isReplying}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReply}
                  loading={isReplying}
                  disabled={!canSendReply}
                >
                  Add reply
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </SupportFooter>
    </>
  );
};

export default SupportTicketDetail;
